/**
 * Timeline sync indicator - confirms an edited timeline actually reached
 * OPBridge (which persists it to disk under this user's OPButtons-scoped
 * data dir AND updates the live engine, both as one side effect of the
 * same `PUT /timelines/{consumer}/{id}` - see `timeline/autoSync.ts`
 * module doc). Operator ask (2026-08-21): a visible confirmation that
 * "saved" really means saved, not just "typed into the browser."
 *
 * 2026-09-29: it also says when something on the timeline WON'T PLAY - a
 * step tied to another computer, a MIDI port that machine does not have, a
 * kind a timeline cannot fire yet. That used to surface only in a Launch
 * toast, or not at all; now it shows while authoring, with the full reason
 * in the tooltip.
 *
 * Same shared pill chassis as ClockSourcePill/OpbridgeBadge
 * (`.bridge-status`, `data-state`-driven LED) - reused deliberately so a
 * new status readout in the header doesn't invent a third visual language.
 */

import { timelineSyncState, timelineSyncLastSavedAt, timelineSyncLastError, timelineSyncWarning } from '../timeline/autoSync.ts';

interface TimelineSyncPillProps {
  id: string;
}

function agoLabel(savedAtMs: number): string {
  const s = Math.max(0, Math.round((Date.now() - savedAtMs) / 1000));
  if (s < 2) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

export function TimelineSyncPill(props: TimelineSyncPillProps) {
  const state = () => timelineSyncState(props.id);
  const warning = () => timelineSyncWarning(props.id);

  const label = (): string => {
    switch (state()) {
      case 'saved': {
        if (warning()) return 'Synced - check steps';
        const at = timelineSyncLastSavedAt(props.id);
        return at ? `Synced ${agoLabel(at)}` : 'Synced';
      }
      case 'saving': return 'Saving…';
      case 'pending': return 'Unsaved edits';
      case 'error': return 'Sync failed';
      case 'idle': default: return warning() ? "Can't play - check steps" : 'Not synced yet';
    }
  };

  // 'none' (not a new state - the existing grey-LED vocabulary
  // `.bridge-status[data-state="down"|"none"|"unpaired"]` already styles)
  // reused for "not synced yet," matching every other status pill's own
  // idle treatment rather than inventing a parallel word for the same look.
  const pillState = (): 'ok' | 'warn' | 'none' | 'error' => {
    switch (state()) {
      case 'saved': return warning() ? 'warn' : 'ok';
      case 'saving':
      case 'pending': return 'warn';
      case 'error': return 'error';
      case 'idle': default: return warning() ? 'warn' : 'none';
    }
  };

  const title = (): string => {
    switch (state()) {
      case 'saved': {
        const w = warning();
        return w
          ? `Saved to OPBridge, but not everything will play: ${w}`
          : 'Saved to OPBridge - on disk and the live engine has it';
      }
      case 'saving': return 'Pushing to OPBridge…';
      case 'pending': return 'Edited - pushes to OPBridge automatically in a moment';
      case 'error': {
        const err = timelineSyncLastError(props.id);
        return err ? `Sync failed: ${err}` : 'Sync failed';
      }
      case 'idle': default: {
        const w = warning();
        return w ? `Nothing on this timeline can play yet: ${w}` : "Hasn't been pushed to OPBridge yet this session";
      }
    }
  };

  return (
    <div
      class="bridge-status timeline-sync-pill"
      data-state={pillState()}
      data-warning={warning() ? 'true' : 'false'}
      title={title()}
      aria-label={`Timeline sync: ${label()}`}
    >
      <span class="led" aria-hidden="true" />
      <span class="msg timeline-sync-pill-msg">{label()}</span>
    </div>
  );
}
